import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Truck, Edit3, Trash2, X, Loader2 } from "lucide-react";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";

const EMPTY = {
    name: "",
    gstin: "",
    contact_person: "",
    phone: "",
    email: "",
    address: "",
    payment_terms_days: 30,
    active: true,
    notes: "",
};

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const AdminVendors = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // "__new__" or vendor id
    const [draft, setDraft] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [activeFilter, setActiveFilter] = useState("all");

    const load = () => {
        setLoading(true);
        const params = {};
        if (activeFilter !== "all") params.active = activeFilter === "active";
        api.get("/admin/vendors", { params })
            .then((r) => setItems(r.data.items || []))
            .finally(() => setLoading(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [activeFilter]);

    const openNew = () => { setEditing("__new__"); setDraft(EMPTY); };
    const openEdit = (v) => { setEditing(v.id); setDraft({ ...EMPTY, ...v }); };

    const save = async () => {
        if (!draft.name?.trim()) { toast.error("Vendor name required"); return; }
        setSaving(true);
        try {
            if (editing === "__new__") {
                await api.post("/admin/vendors", draft);
                toast.success("Vendor created");
            } else {
                const patch = { ...draft };
                ["id", "created_at", "updated_at", "created_by", "total_purchased_amount"].forEach((k) => delete patch[k]);
                await api.patch(`/admin/vendors/${editing}`, patch);
                toast.success("Vendor updated");
            }
            setEditing(null);
            load();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Could not save");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (v) => {
        if (!window.confirm(`Delete vendor "${v.name}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/vendors/${v.id}`);
            toast.success("Vendor deleted");
            load();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Could not delete. Try marking inactive instead.");
        }
    };

    const toggleActive = async (v) => {
        try {
            await api.patch(`/admin/vendors/${v.id}`, { active: !v.active });
            toast.success(`${v.name} ${v.active ? "deactivated" : "activated"}`);
            load();
        } catch { toast.error("Failed"); }
    };

    return (
        <AdminAuthGuard>
            <AdminLayout title="Vendors" subtitle="Suppliers & purchase order targets">
                <div className="mb-5 flex flex-wrap items-center gap-3">
                    <div className="flex gap-1.5" data-testid="admin-vendors-filter">
                        {["all", "active", "inactive"].map((f) => (
                            <button
                                key={f}
                                onClick={() => setActiveFilter(f)}
                                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                                    activeFilter === f ? "bg-brand-700 text-white" : "border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"
                                }`}
                                data-testid={`admin-vendors-filter-${f}`}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                    <button onClick={openNew} className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-800" data-testid="admin-vendors-new">
                        <Plus className="h-4 w-4" /> New vendor
                    </button>
                </div>

                {loading ? (
                    <div className="h-48 skeleton rounded-2xl" />
                ) : items.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-zinc-200 bg-white p-12 text-center text-sm text-slate-500" data-testid="admin-vendors-empty">
                        <Truck className="mx-auto h-8 w-8 text-slate-300" />
                        <p className="mt-2">No vendors yet. Add suppliers to issue purchase orders.</p>
                    </div>
                ) : (
                    <div className="overflow-hidden rounded-2xl border border-zinc-100 bg-white shadow-sm">
                        <table className="w-full text-sm" data-testid="admin-vendors-table">
                            <thead className="border-b border-zinc-100 bg-zinc-50 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                                <tr>
                                    <th className="px-4 py-3">Name</th>
                                    <th className="px-4 py-3">GSTIN</th>
                                    <th className="px-4 py-3">Contact</th>
                                    <th className="px-4 py-3">Terms</th>
                                    <th className="px-4 py-3 text-right">Total purchased</th>
                                    <th className="px-4 py-3">Status</th>
                                    <th className="px-4 py-3"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-50">
                                {items.map((v) => (
                                    <tr key={v.id} className="hover:bg-zinc-50" data-testid={`admin-vendor-row-${v.id}`}>
                                        <td className="px-4 py-3">
                                            <p className="font-medium text-zinc-900">{v.name}</p>
                                            <p className="text-xs text-slate-500">{v.email}</p>
                                        </td>
                                        <td className="px-4 py-3 font-mono text-xs">{v.gstin || "—"}</td>
                                        <td className="px-4 py-3 text-xs">
                                            <p>{v.contact_person || "—"}</p>
                                            <p className="text-slate-500">{v.phone}</p>
                                        </td>
                                        <td className="px-4 py-3 text-xs">Net {v.payment_terms_days}</td>
                                        <td className="px-4 py-3 text-right text-xs font-semibold">{inr(v.total_purchased_amount)}</td>
                                        <td className="px-4 py-3">
                                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${v.active ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>
                                                {v.active ? "Active" : "Inactive"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-end gap-1">
                                                <button onClick={() => openEdit(v)} className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-100" title="Edit" data-testid={`admin-vendor-edit-${v.id}`}><Edit3 className="h-3.5 w-3.5" /></button>
                                                <button onClick={() => toggleActive(v)} className="rounded-md px-2 py-1 text-[10px] font-medium text-zinc-600 hover:bg-zinc-100" data-testid={`admin-vendor-toggle-${v.id}`}>
                                                    {v.active ? "Deactivate" : "Activate"}
                                                </button>
                                                <button onClick={() => remove(v)} className="rounded-md p-1.5 text-rose-500 hover:bg-rose-50" title="Delete" data-testid={`admin-vendor-delete-${v.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {editing && (
                    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
                        <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl" data-testid="admin-vendor-modal">
                            <div className="flex items-center justify-between">
                                <h2 className="text-lg font-semibold text-zinc-900">{editing === "__new__" ? "New vendor" : `Edit ${draft.name}`}</h2>
                                <button onClick={() => setEditing(null)} className="rounded-full p-1.5 text-zinc-400 hover:bg-zinc-100"><X className="h-4 w-4" /></button>
                            </div>
                            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                                <label className="col-span-2"><span className="text-xs text-zinc-500">Name *</span><input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5" data-testid="admin-vendor-form-name" /></label>
                                <label><span className="text-xs text-zinc-500">GSTIN</span><input value={draft.gstin || ""} onChange={(e) => setDraft({ ...draft, gstin: e.target.value.toUpperCase() })} placeholder="33ABCDE1234F1Z5" maxLength={15} className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5 font-mono" data-testid="admin-vendor-form-gstin" /></label>
                                <label><span className="text-xs text-zinc-500">Payment terms (days)</span><input type="number" min="0" value={draft.payment_terms_days} onChange={(e) => setDraft({ ...draft, payment_terms_days: Number(e.target.value) })} className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5" data-testid="admin-vendor-form-terms" /></label>
                                <label><span className="text-xs text-zinc-500">Contact person</span><input value={draft.contact_person || ""} onChange={(e) => setDraft({ ...draft, contact_person: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5" data-testid="admin-vendor-form-contact" /></label>
                                <label><span className="text-xs text-zinc-500">Phone</span><input value={draft.phone || ""} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5" data-testid="admin-vendor-form-phone" /></label>
                                <label className="col-span-2"><span className="text-xs text-zinc-500">Email</span><input type="email" value={draft.email || ""} onChange={(e) => setDraft({ ...draft, email: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5" data-testid="admin-vendor-form-email" /></label>
                                <label className="col-span-2"><span className="text-xs text-zinc-500">Address</span><input value={draft.address || ""} onChange={(e) => setDraft({ ...draft, address: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5" data-testid="admin-vendor-form-address" /></label>
                                <label className="col-span-2"><span className="text-xs text-zinc-500">Notes</span><textarea rows={2} value={draft.notes || ""} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-200 p-2 text-sm" /></label>
                                <label className="col-span-2 inline-flex items-center gap-2 text-xs">
                                    <input type="checkbox" checked={draft.active !== false} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} data-testid="admin-vendor-form-active" />
                                    <span>Active (eligible to receive purchase orders)</span>
                                </label>
                            </div>
                            <div className="mt-5 flex items-center justify-end gap-2">
                                <button onClick={() => setEditing(null)} className="rounded-md border border-zinc-200 px-3 py-1.5 text-sm hover:bg-zinc-50" data-testid="admin-vendor-modal-cancel">Cancel</button>
                                <button onClick={save} disabled={saving} className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-50" data-testid="admin-vendor-modal-save">
                                    {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                                    {saving ? "Saving…" : "Save"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </AdminLayout>
        </AdminAuthGuard>
    );
};

export default AdminVendors;
